/* ─────────────────────────────────────────────────────────────────
   MLW Quiz State Machine — Shared Stage Engine
   ─────────────────────────────────────────────────────────────────
   Phase 1 brand-agnostic engine powering both quiz variants:

     • TMT: linear 6-question Likert flow, auto-advance, results via
            redirect to /results?id=xxx
     • MLW: branching yes/no flow (showWhen conditionals), results
            rendered inline on same page

   The engine is data-driven — it consumes a questions[] array
   defined by the section schema and fires stage-change callbacks.
   UI (question renderers, progress bar, email capture) is wired
   separately by the section liquid.

   STAGES
     landing → questions → (optional info-card) → email → results

   PUBLIC API (exposed on window.MLWQuiz)
     MLWQuiz.init(config)           — bootstraps the engine
     MLWQuiz.next()                 — advance one step (respects showWhen)
     MLWQuiz.back()                 — back one step (respects showWhen)
     MLWQuiz.setAnswer(i, value)    — store answer at question index i
     MLWQuiz.getAnswers()           — returns the answers[] array
     MLWQuiz.getCurrentStage()      — returns stage string
     MLWQuiz.onStageChange(fn)      — subscribe to stage changes
   ───────────────────────────────────────────────────────────────── */

(function (window) {
  "use strict";

  function createQuiz() {
    var state = {
      stage: "landing",
      currentQuestionIndex: 0,
      answers: [],
      config: null,
      listeners: [],
      autoAdvanceTimer: null,
    };

    /* Scroll-to-top on every stage transition (predictable mobile UX). */
    function scrollToTop() {
      if (typeof window !== "undefined" && window.scrollTo) {
        try { window.scrollTo({ top: 0, behavior: "smooth" }); }
        catch (e) { window.scrollTo(0, 0); }
      }
    }

    function emit() {
      for (var i = 0; i < state.listeners.length; i++) {
        try {
          state.listeners[i]({
            stage: state.stage,
            currentQuestionIndex: state.currentQuestionIndex,
            answers: state.answers.slice(),
          });
        } catch (e) { /* listener errors must not break the engine */ }
      }
    }

    function setStage(nextStage) {
      state.stage = nextStage;
      scrollToTop();
      emit();
    }

    /* ─── Branching visibility ─────────────────────────────────
       A question is visible unless it has a showWhen rule that
       doesn't match. showWhen = { questionId, value } OR
       { questionIndex, value }. For a linear flow (TMT) no
       question declares showWhen, so every question is visible. */
    function isVisible(question) {
      if (!question || !question.showWhen) return true;
      var rule = question.showWhen;
      var idx = -1;
      if (typeof rule.questionIndex === "number") {
        idx = rule.questionIndex;
      } else if (rule.questionId && state.config && state.config.questions) {
        for (var i = 0; i < state.config.questions.length; i++) {
          if (state.config.questions[i].id === rule.questionId) { idx = i; break; }
        }
      }
      if (idx < 0) return false;
      var prior = state.answers[idx];
      if (prior && typeof prior === "object" && "value" in prior) {
        return prior.value === rule.value;
      }
      return prior === rule.value;
    }

    /* Walk questions in `direction` (1 = forward, -1 = back) from
       the current index, skipping invisible ones. Returns the next
       visible index or -1 if none remain in that direction. */
    function findNextVisibleIdx(direction) {
      if (!state.config || !state.config.questions) return -1;
      var qs = state.config.questions;
      var i = state.currentQuestionIndex + direction;
      while (i >= 0 && i < qs.length) {
        if (isVisible(qs[i])) return i;
        i += direction;
      }
      return -1;
    }

    /* ─── Transition helpers ──────────────────────────────────── */
    function goToFirstVisibleQuestion() {
      if (!state.config || !state.config.questions || !state.config.questions.length) {
        goToEmailOrResults();
        return;
      }
      var qs = state.config.questions;
      for (var i = 0; i < qs.length; i++) {
        if (isVisible(qs[i])) { state.currentQuestionIndex = i; setStage("questions"); return; }
      }
      goToEmailOrResults();
    }

    function goToEmailOrResults() {
      if (state.config && state.config.showInfoCard) {
        setStage("info-card");
      } else if (state.config && state.config.emailMode === "skippable" && state.config.hasPrefilledEmail) {
        transitionToResults();
      } else {
        setStage("email");
      }
    }

    /* Results mode fork — redirect (TMT) vs inline (MLW). */
    function transitionToResults() {
      var cfg = state.config || {};
      var payload = {
        answers: state.answers.slice(),
        email: cfg.email || "",
        name: cfg.name || "",
      };
      if (typeof cfg.onComplete === "function") {
        try { cfg.onComplete(payload); } catch (e) {}
      }
      if (cfg.resultsMode === "redirect") {
        setStage("results");
        if (typeof cfg.onPersistAndRedirect === "function") {
          try { cfg.onPersistAndRedirect(payload); } catch (e) {}
        } else if (cfg.resultsRedirectUrl) {
          window.location.href = cfg.resultsRedirectUrl;
        }
      } else {
        setStage("results");
      }
    }

    /* ─── Auto-advance timer (TMT Likert taps) ────────────────── */
    function clearAutoAdvance() {
      if (state.autoAdvanceTimer) {
        clearTimeout(state.autoAdvanceTimer);
        state.autoAdvanceTimer = null;
      }
    }
    function scheduleAutoAdvance() {
      var cfg = state.config || {};
      if (!cfg.autoAdvance) return;
      clearAutoAdvance();
      var delay = typeof cfg.autoAdvanceDelay === "number" ? cfg.autoAdvanceDelay : 220;
      state.autoAdvanceTimer = setTimeout(function () { next(); }, delay);
    }

    /* ─── Public API ──────────────────────────────────────────── */
    function init(config) {
      state.config = config || {};
      state.answers = [];
      state.currentQuestionIndex = 0;
      state.stage = "landing";
      clearAutoAdvance();
      emit();
    }

    function next() {
      clearAutoAdvance();
      if (state.stage === "landing") { goToFirstVisibleQuestion(); return; }
      if (state.stage === "questions") {
        var nextIdx = findNextVisibleIdx(1);
        if (nextIdx === -1) { goToEmailOrResults(); return; }
        state.currentQuestionIndex = nextIdx;
        setStage("questions");
        return;
      }
      if (state.stage === "info-card") {
        var cfg = state.config || {};
        if (cfg.emailMode === "skippable" && cfg.hasPrefilledEmail) {
          transitionToResults();
        } else {
          setStage("email");
        }
        return;
      }
      if (state.stage === "email") { transitionToResults(); return; }
    }

    function back() {
      clearAutoAdvance();
      if (state.stage === "questions") {
        var prevIdx = findNextVisibleIdx(-1);
        if (prevIdx === -1) { setStage("landing"); return; }
        /* Clear the answer we're walking away from so the user can
           re-answer cleanly; matches MLW's back-button behavior. */
        state.answers[state.currentQuestionIndex] = undefined;
        state.currentQuestionIndex = prevIdx;
        setStage("questions");
        return;
      }
      if (state.stage === "info-card") {
        /* Find last visible question and return to it. */
        var qs = (state.config && state.config.questions) || [];
        for (var i = qs.length - 1; i >= 0; i--) {
          if (isVisible(qs[i])) { state.currentQuestionIndex = i; setStage("questions"); return; }
        }
        setStage("landing");
        return;
      }
      if (state.stage === "email") {
        if (state.config && state.config.showInfoCard) setStage("info-card");
        else {
          var qs2 = (state.config && state.config.questions) || [];
          for (var j = qs2.length - 1; j >= 0; j--) {
            if (isVisible(qs2[j])) { state.currentQuestionIndex = j; setStage("questions"); return; }
          }
          setStage("landing");
        }
        return;
      }
      if (state.stage === "results") { setStage("email"); return; }
    }

    function setAnswer(questionIndex, value) {
      state.answers[questionIndex] = value;
      emit();
      /* Auto-advance is intentionally decoupled from setAnswer so
         renderers can choose to schedule it (Likert) or not (Choice
         with explicit Next button). */
    }

    function setAnswerAndAdvance(questionIndex, value) {
      setAnswer(questionIndex, value);
      scheduleAutoAdvance();
    }

    function getAnswers()       { return state.answers.slice(); }
    function getCurrentStage()  { return state.stage; }
    function getCurrentQuestionIndex() { return state.currentQuestionIndex; }
    function getConfig()        { return state.config; }
    function getVisibleQuestionCount() {
      var qs = (state.config && state.config.questions) || [];
      var count = 0;
      for (var i = 0; i < qs.length; i++) if (isVisible(qs[i])) count++;
      return count;
    }
    function getAnsweredVisibleCount() {
      var qs = (state.config && state.config.questions) || [];
      var count = 0;
      for (var i = 0; i < qs.length; i++) {
        if (isVisible(qs[i]) && state.answers[i] !== undefined) count++;
      }
      return count;
    }
    function onStageChange(fn)  {
      if (typeof fn === "function") state.listeners.push(fn);
    }

    return {
      init: init,
      next: next,
      back: back,
      setAnswer: setAnswer,
      setAnswerAndAdvance: setAnswerAndAdvance,
      getAnswers: getAnswers,
      getCurrentStage: getCurrentStage,
      getCurrentQuestionIndex: getCurrentQuestionIndex,
      getConfig: getConfig,
      getVisibleQuestionCount: getVisibleQuestionCount,
      getAnsweredVisibleCount: getAnsweredVisibleCount,
      findNextVisibleIdx: findNextVisibleIdx,
      onStageChange: onStageChange,
    };
  }

  window.MLWQuiz = window.MLWQuiz || createQuiz();
  window.MLWQuizCreate = createQuiz;
})(window);
